import { APPLE_PAY_BRIDGE_POLICY } from '../apple-pay-policy';

const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
const APPLE_PAY_MODAL_SELECTOR = 'apple-pay-modal';
const APPLE_PAY_MODAL_FOCUS_STYLE_ID = 'paypercut-apple-pay-modal-focus-style';
const APPLE_PAY_MODAL_FOCUS_SELECTOR =
  '[data-modal-element-overlay]:focus, [data-modal-element-overlay]:focus-visible, ' +
  '.modal-overlay:focus, .modal-overlay:focus-visible';

let pendingLoad: Promise<void> | null = null;
let modalObserver: MutationObserver | null = null;
let modalObserverUsers = 0;

/**
 * Makes the cross-browser Apple Pay API available on the merchant page.
 *
 * Safari exposes ApplePaySession natively. Other supported browsers use
 * Apple's SDK, which still has to run in the top-level merchant context.
 * Loading the SDK is not a payment action and does not consume user activation.
 */
export function ensureApplePaySDK(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }

  if ((window as any).ApplePaySession) return Promise.resolve();
  if (pendingLoad) return pendingLoad;

  pendingLoad = new Promise<void>((resolve) => {
    const existing = Array.from(document.scripts).find(
      (script) => script.src === APPLE_PAY_SDK_URL,
    );
    const script = existing ?? document.createElement('script');
    const settle = () => resolve();

    script.addEventListener('load', settle, { once: true });
    script.addEventListener('error', settle, { once: true });
    if (!existing) {
      script.src = APPLE_PAY_SDK_URL;
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.dataset.paypercutApplePaySdk = 'true';
      document.head.appendChild(script);
    }
  });

  return pendingLoad;
}

export function acquireApplePayModalObserver(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  observeApplePayModals();
  modalObserverUsers += 1;
}

export function releaseApplePayModalObserver(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (modalObserverUsers <= 0) {
    modalObserverUsers = 0;
    return;
  }
  modalObserverUsers -= 1;
  if (modalObserverUsers === 0) {
    modalObserver?.disconnect();
    modalObserver = null;
  }
}

/**
 * Apple's cross-browser SDK mounts <apple-pay-modal> in the merchant document.
 * The element delegates focus to a non-interactive overlay inside an open
 * shadow root. Chromium gives that overlay its default blue focus outline,
 * which looks like an accidental selection border around the whole modal.
 *
 * Keep this scoped to the overlay: actionable controls inside Apple's modal
 * retain their own keyboard focus treatment.
 */
function observeApplePayModals(): void {
  document.querySelectorAll(APPLE_PAY_MODAL_SELECTOR).forEach((modal) => {
    patchApplePayModalFocus(modal);
  });

  if (modalObserver || !document.documentElement) return;

  modalObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches(APPLE_PAY_MODAL_SELECTOR)) {
          patchApplePayModalFocus(node);
        }
        node.querySelectorAll(APPLE_PAY_MODAL_SELECTOR).forEach((modal) => {
          patchApplePayModalFocus(modal);
        });
      });
    });
  });
  modalObserver.observe(document.documentElement, { childList: true, subtree: true });
}

function patchApplePayModalFocus(modal: Element, attempts = 0): void {
  const root = modal.shadowRoot;
  if (!root) {
    // Apple's custom element attaches its shadow root immediately after the
    // host is inserted. Allow a few frames for that upgrade without polling
    // for the lifetime of the page.
    if (attempts < APPLE_PAY_BRIDGE_POLICY.modalUpgradeAnimationFrames) {
      requestAnimationFrame(() => patchApplePayModalFocus(modal, attempts + 1));
    }
    return;
  }

  if (root.getElementById(APPLE_PAY_MODAL_FOCUS_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = APPLE_PAY_MODAL_FOCUS_STYLE_ID;
  style.textContent = `${APPLE_PAY_MODAL_FOCUS_SELECTOR} { outline: none !important; }`;
  root.appendChild(style);
}
